import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw, Sparkles, X } from "lucide-react";

const POLL_INTERVAL_MS = 60_000; // poll /api/version toutes les 60s
const STORAGE_KEY = "blitzz_build_id";

/**
 * Banner global qui detecte un nouveau deploiement et propose un reload propre :
 *   1) Lit /api/version au montage et stocke le build_id dans localStorage
 *   2) Repolle toutes les 60s, compare au build_id stocke
 *   3) Si different -> affiche le banner avec bouton "Mettre a jour"
 *   4) Au clic : envoie messages SKIP_WAITING + CLEAR_CACHES au Service Worker,
 *      vide localStorage cache, force reload (window.location.reload())
 *
 * S'utilise une seule fois dans App.js (placed in layout root).
 */
export default function VersionUpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const initialBuildIdRef = useRef(null);
  const intervalRef = useRef(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL || "";

  const fetchVersion = useCallback(async () => {
    try {
      // pas de cache, no-store pour s'assurer qu'on lit toujours la prod
      const r = await fetch(`${apiUrl}/api/version`, {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      if (!r.ok) return null;
      return await r.json();
    } catch {
      return null;
    }
  }, [apiUrl]);

  // Initialisation : recupere le build_id de demarrage et stocke dans ref + localStorage
  useEffect(() => {
    let mounted = true;
    // Respecter le toggle "Detection automatique" (parametres > Securite)
    if (localStorage.getItem("blitzz_autocache_enabled") === "false") {
      return; // disabled : ne pas polling, ne pas afficher le banner
    }
    (async () => {
      const v = await fetchVersion();
      if (!mounted || !v) return;
      const stored = localStorage.getItem(STORAGE_KEY);
      // Si rien en cache, on stocke (premier login)
      // Si different : update detecte des le mount (cas user qui revient apres deploy)
      if (!stored) {
        localStorage.setItem(STORAGE_KEY, v.build_id);
        initialBuildIdRef.current = v.build_id;
      } else if (stored !== v.build_id) {
        // Update detecte des l'arrivee : afficher le banner
        initialBuildIdRef.current = stored; // garder l'ancien comme reference
        setUpdateAvailable(true);
      } else {
        initialBuildIdRef.current = v.build_id;
      }
    })();
    return () => { mounted = false; };
  }, [fetchVersion]);

  // Poll toutes les 60s
  useEffect(() => {
    if (updateAvailable) {
      // Inutile de poller une fois la maj detectee
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(async () => {
      const v = await fetchVersion();
      if (!v) return;
      const ref = initialBuildIdRef.current;
      if (ref && v.build_id !== ref) {
        setUpdateAvailable(true);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [updateAvailable, fetchVersion]);

  // Listener pour message du Service Worker quand caches vides
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === "CACHES_CLEARED") {
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // 1. Mettre a jour le build_id stocke (sinon on retomberait dans le banner apres reload)
      const v = await fetchVersion();
      if (v?.build_id) {
        localStorage.setItem(STORAGE_KEY, v.build_id);
      }
      // 2. Si SW dispo : demander skipWaiting + clear caches via message
      //    Le SW renvoie CACHES_CLEARED -> handler reload la page
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          // Si une nouvelle version est en attente, l'activer
          if (reg.waiting) {
            reg.waiting.postMessage({ type: "SKIP_WAITING" });
          }
          // Vider tous les caches
          if (reg.active) {
            reg.active.postMessage({ type: "CLEAR_CACHES" });
            // Fallback : si SW ne repond pas en 1.5s, on reload quand meme
            setTimeout(() => window.location.reload(), 1500);
            return;
          }
        }
      }
      // 3. Pas de SW : clear caches API + reload
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      window.location.reload();
    } catch (e) {
      // En cas d'erreur, on tente quand meme un reload simple
      window.location.reload();
    }
  }, [fetchVersion]);

  if (!updateAvailable) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] max-w-md w-[calc(100vw-2rem)] md:w-auto"
      data-testid="version-update-banner"
      role="alert"
    >
      <div
        className="rounded-xl px-4 py-3 flex items-center gap-3 backdrop-blur-md shadow-2xl"
        style={{
          background: "linear-gradient(135deg, rgba(247, 201, 53, 0.95), rgba(230, 184, 37, 0.95))",
          color: "#1a1025",
          boxShadow: "0 12px 32px rgba(247, 201, 53, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.08)",
        }}
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a1025]/15 flex items-center justify-center">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold leading-tight">Nouvelle version disponible</div>
          <div className="text-[10px] opacity-80 mt-0.5">Rechargez pour voir les dernieres ameliorations</div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex-shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-lg bg-[#1a1025] text-[#f7c935] hover:bg-[#0b0811] transition-colors flex items-center gap-1.5 disabled:opacity-50"
          data-testid="version-update-refresh-btn"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "..." : "Mettre a jour"}
        </button>
        <button
          onClick={() => setUpdateAvailable(false)}
          className="flex-shrink-0 p-1 rounded hover:bg-[#1a1025]/10 transition-colors"
          aria-label="Reporter"
          data-testid="version-update-dismiss-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Helper exporte : permet a une page de declencher manuellement le clear cache + reload.
 * Utilise dans TenantSettingsPage > "Vider le cache et recharger".
 */
export async function forceClearCacheAndReload() {
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.unregister();
      }
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    // Vider le localStorage du build_id
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  // Reload force (sans cache)
  window.location.reload();
}
